import { useEffect, useState } from "react";
import { getFreshToken } from "./auth.js";
import { useStudio } from "./StudioContext.js";
import { Section, TextField, useYField } from "./form_primitives.js";
import { contract, formLabel as label, seasonYears } from "./techpack_form_contract.js";
import { lockCanonicalVersion } from "./canonical_actions.js";

type CategoryCode={code:string;label_en?:string;label_zh?:string;status:string};

function RegistryCategoryField(){
  const {techPack,languageCode:lang,roomId}=useStudio();
  const [category,setCategory]=useYField(techPack.style_id,"category");
  const [rows,setRows]=useState<CategoryCode[]>([]),[adding,setAdding]=useState(false),[newName,setNewName]=useState(""),[error,setError]=useState("");
  async function request(action:string,body?:unknown){
    const token=await getFreshToken();
    const response=await fetch(`/api/studio/techpacks/${roomId}/canonical/${action}`,{method:body===undefined?"GET":"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${token||""}`},body:body===undefined?undefined:JSON.stringify(body)});
    if(!response.ok)throw new Error(label("canonical_failed",lang));
    return response.json();
  }
  useEffect(()=>{let live=true;void request("registry").then(data=>{if(live)setRows((data.segments?.category||[]).filter((row:CategoryCode)=>row.status!=="deprecated"));}).catch(()=>{});return()=>{live=false;};},[roomId]);
  async function add(){
    const name=newName.trim();if(!name)return;setError("");
    try{const data=await request("add_registry",{label_en:name});const row:CategoryCode={code:data.code,label_en:data.label_en,label_zh:data.label_zh,status:"adopted"};setRows(old=>[...old,row]);setCategory(row.label_en||name);setNewName("");setAdding(false);}
    catch{setError(label("canonical_failed",lang));}
  }
  return <div className="sf-mb2"><label className="sf-label">{label("garment_category",lang)}
    <select className={`sf-select sf-select-category${lang==="zh"?" sf-select-zh-compact":""}`} value={category} onChange={e=>setCategory(e.target.value)}>
      <option value="">{label("select",lang)}</option>
      {category&&!rows.some(row=>row.label_en===category)&&<option value={category}>{category}</option>}
      {rows.map(row=><option key={row.code} value={row.label_en||row.code}>{lang==="zh"?(row.label_zh||row.label_en||row.code):(row.label_en||row.code)}</option>)}
    </select></label>
    <button type="button" className="sf-btn" onClick={()=>{setAdding(!adding);setError("");}}>{label("add",lang)}</button>
    {adding&&<div className="sf-flex-row"><input className="sf-input" aria-label={label("garment_category",lang)} placeholder={label("garment_category",lang)} value={newName} onChange={e=>setNewName(e.target.value)} />
      <button type="button" className="sf-btn" onClick={()=>void add()}>{label("confirm",lang)}</button>{error&&<span role="alert">{error}</span>}</div>}
  </div>;
}

/** Presentation changes retain the existing English metadata keys. */
export function StyleHeaderSection() {
  const { techPack, languageCode: lang, roomId, userId } = useStudio();
  const meta = techPack.metadata;
  const [category, setCategory] = useYField(meta, "product_category");
  const [season, setSeason] = useYField(meta, "season");
  const [styleNumber] = useYField(meta, "style_number");
  // Version mirror maintained by CanonicalActions' state refresh: a non-empty
  // _canonical_version_id means a locked (Final) version exists. Excluded from
  // draft change detection in canonical_draft.ts -- mirror, not content.
  const [versionId] = useYField(meta, "_canonical_version_id");
  const locked = !!versionId;
  const match = /^(SS|AW)(\d{2})$/.exec(season);
  const type = match?.[1] ?? "custom";
  const year = match ? String(2000 + Number(match[2])) : String(new Date().getFullYear());
  const years = [...new Set([year, ...seasonYears()])];
  return <Section title={label("product_category", lang)}>
    <select className="sf-select sf-mb2" aria-label={label("product_category", lang)} value={category}
      onChange={e => setCategory(e.target.value)}>
      <option value="">—</option>
      {category && !contract.categories.some(c => c.value === category) && <option value={category}>{category}</option>}
      {contract.categories.map(c => <option key={c.value} value={c.value} disabled={!c.enabled}>
        {label(c.value, lang)}{c.enabled ? "" : ` — ${label("coming_soon", lang)}`}
      </option>)}
    </select>
    <div className="sf-mb2"><label className="sf-label">{label("style_number", lang)}</label>
      <input className="sf-input" readOnly aria-label={label("style_number",lang)} value={styleNumber} /></div>
    <div className="sf-mb2"><label className="sf-label">{label("version", lang)}</label>
      <select className="sf-select" aria-label={label("version", lang)} value={locked ? "final" : "draft"}
        onChange={e => { if (e.target.value === "final" && !locked) void lockCanonicalVersion(techPack, roomId, userId, lang).catch(() => window.alert(label("canonical_failed", lang))); }}>
        <option value="draft">{label("draft", lang)}</option>
        <option value="final" disabled={!styleNumber}>{label("final", lang)}</option>
      </select></div>
    <TextField ymap={meta} fieldKey="project_name" label={label("style_name", lang)} className="sf-mb2" />
    <label className="sf-label">{label("season", lang)}</label>
    <div className="sf-grid-2 sf-mb2">
      <select className={`sf-select sf-select-compact${lang==="zh"?" sf-select-zh-compact":""}`} aria-label={label("season", lang)} value={type}
        onChange={e => setSeason(e.target.value === "custom" ? "" : e.target.value + year.slice(-2))}>
        {contract.season_types.map(t => <option key={t}>{t}</option>)}
        <option value="custom">{label("custom", lang)}</option>
      </select>
      {type === "custom" ? <input className="sf-input" value={season} aria-label={label("custom", lang)} onChange={e => setSeason(e.target.value)} /> :
        <select className="sf-select" value={year} aria-label={label("year", lang)} onChange={e => setSeason(type + e.target.value.slice(-2))}>
          {years.map(y => <option key={y}>{y}</option>)}
        </select>}
    </div>
    <div className="sf-grid-2">
      <RegistryCategoryField />
      <TextField ymap={techPack.style_id} fieldKey="style_type" label={label("style_type", lang)} />
      <TextField ymap={techPack.style_id} fieldKey="silhouette" label={label("silhouette", lang)} />
      {["length","closure","lining"].map(key=><TextField key={key} ymap={techPack.style_id} fieldKey={key} label={label(key,lang)} />)}
    </div>
  </Section>;
}
